import { Component, OnDestroy } from '@angular/core';
import { ConnectionPointDto, ConnectionPointService } from '@app/api/nts';
import { ConnectionRight } from '@shared/interfaces/nts/connection-right';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { Subscription } from 'rxjs';
import { CardComponent } from '@shared/components/card/card.component';
import { ValueDetailsComponent } from '@shared/components/value-details/value-details.component';
import {
  connectionpointLocationDetailsMasterData
} from '@shared/interfaces/nts/connectionpointdata/connectionpoint-location-details.masterdata';
import { connectionpointDetailsMasterdata } from '@shared/interfaces/nts/connectionpointdata/connectionpoint-details.masterdata';
import {
  connectionpointKeyDetailsMasterData
} from '@shared/interfaces/nts/connectionpointdata/connectionpoint-key-details.masterdata';
import { ModalService } from '@app/core/services/modal.service';
import { HeaderService } from '@app/core/services/header.service';
import { TranslatePipe } from '@ngx-translate/core';
import { Columns } from '@shared/interfaces/table/column-config';
import { StateManagerService } from '@shared/state/state-manager.service';
import {
  ConnectionpointEditComponent
} from '@shared/modals/connectionpoint/connectionpoint-edit/connectionpoint-edit.component';
import {
  connectionpointOverviewActionMenu
} from '@views/connectionpoint/connectionpoint-overview/connectionpoint-overview.action-menu';
import { hasRole } from '@app/core/guards/has-role.guard';
import { AuthRoles } from '@app/api/dmc';
import { connectionRightDetailsMasterdata } from '@shared/interfaces/nts/connection-right-details.masterdata';
import { systemServicesDetailsMasterdata } from '@shared/interfaces/nts/system-services-details.masterdata';
import { connectionpointSharedInstallationMasterdata } from '@shared/interfaces/nts/connectionpointdata/connectionpoint-shared-installation.masterdata';

@Component({
  selector: 'app-connectionpoint-search-overview',
  imports: [
    CardComponent,
    ValueDetailsComponent,
    TranslatePipe,
  ],
  templateUrl: './connectionpoint-overview.component.html',
  styleUrl: './connectionpoint-overview.component.css'
})
export class ConnectionpointOverviewComponent implements OnDestroy {
  public id?: string;

  public connectionPoint?: ConnectionPointDto;
  public connectionRight?: ConnectionRight;

  public locationMasterData: Columns<ConnectionPointDto>[] = connectionpointLocationDetailsMasterData;
  public connectionpointDetailsMasterData: Columns<ConnectionPointDto>[] = connectionpointDetailsMasterdata;
  public keyMasterData: Columns<ConnectionPointDto>[] = connectionpointKeyDetailsMasterData;
  public connectionRightDetailsMasterdata = connectionRightDetailsMasterdata;
  public systemServicesDetailsMasterdata = systemServicesDetailsMasterdata;
  public sharedInstallationMasterdata = connectionpointSharedInstallationMasterdata;
  private readonly subscriptions = new Subscription();
  private connectionRightLoadVersion = 0;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly modalService: ModalService,
    private readonly headerMenuService: HeaderService,
    private readonly stateManagerService: StateManagerService,
    private readonly connectionPointService: ConnectionPointService,
  ) {
    const cpWriter = hasRole([AuthRoles.ConnectionPointWriter]);

    this.subscriptions.add(this.route.paramMap.subscribe((q: ParamMap) => {
      this.id = q.get('id')!;
      this.connectionRight = undefined;

      this.headerMenuService.setActions(
        connectionpointOverviewActionMenu,
        {
          edit: () => this.openEditModal(),
        },
        {
          edit: () => !cpWriter,
        }
      );

      this.fetchAndUpdateConnectionPoint();
    }));

    this.subscriptions.add(toObservable(this.stateManagerService.connectionPointState).subscribe(connectionPoint => {
      if (connectionPoint && this.isCurrentConnectionPoint(connectionPoint)) {
        this.updateConnectionPoint(connectionPoint);
      }
    }));
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public fetchAndUpdateConnectionPoint(): void {
    this.stateManagerService.refreshConnectionPoint(this.id!).subscribe();
  }

  public openEditModal(): void {
    this.modalService.show({
      component: ConnectionpointEditComponent,
      title: 'action.edit',
      actions: [
        {
          type: 'primary',
          label: 'system.button.edit',
          method: 'submit'
        },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: { id: this.id, connectionPoint: this.connectionPoint }
    }).subscribe(saved => {
      if (saved) {
        this.stateManagerService.refreshConnectionPoint(this.id!).subscribe();
      }
    });
  }

  private updateConnectionPoint(connectionPoint: ConnectionPointDto): void {
    this.connectionPoint = connectionPoint;
    this.connectionRight = undefined;
    const loadVersion = ++this.connectionRightLoadVersion;

    if (!connectionPoint.id || !connectionPoint.connectionPointOwnsConnectionRights) {
      this.connectionRight = undefined;
      return;
    }

    this.subscriptions.add(
      this.connectionPointService.connectionPointConnectionPointIdConnectionRightGet(connectionPoint.id)
        .subscribe(right => {
          if (loadVersion === this.connectionRightLoadVersion
            && this.connectionPoint?.id === connectionPoint.id
            && this.connectionPoint?.connectionPointOwnsConnectionRights) {
            this.connectionRight = right ?? undefined;
          }
        })
    );
  }

  private isCurrentConnectionPoint(connectionPoint: ConnectionPointDto): boolean {
    return connectionPoint.id === this.id || connectionPoint.identification === this.id;
  }
}
